/**
 * CR204: Search & Filters Block
 * CR315: Updated to use SearchFilterConfig from layout_json
 * CR316: Added search suggestions and recent searches support
 * 
 * Renders the search bar and filter dropdowns based on layout configuration.
 * This is the AUTHORITATIVE search/filter UI - replaces all legacy search components.
 * 
 * IMPORTANT: Styling must match DealFinderPreview.tsx renderSearchFilters()
 */

import { useState, useCallback, useRef } from "react";
import type { BlockProps } from "./types";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMerchantCategories } from "@/hooks/useMerchantCategories";
import { useRecentSearches } from "@/hooks/useRecentSearches";
import { useSearchSuggestions } from "@/hooks/useSearchSuggestions";
import { SearchDropdownPanel } from "./SearchDropdownPanel";
import type { FilterControl, SearchFilterConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_FILTER_CONFIG } from "@/lib/layout-studio/types";

interface SearchFiltersConfig {
  // Future: additional filter configurations from Layout Studio
}

export function SearchFiltersBlock({ config, context }: BlockProps<SearchFiltersConfig>) {
  const { categories, loading: categoriesLoading } = useMerchantCategories({ deals: context.deals });
  
  // CR315: Get search config from layout
  const searchFilter = context.layout?.filters?.find((f) => f.id === "search");
  const searchConfig = (searchFilter?.config as SearchFilterConfig) || DEFAULT_SEARCH_FILTER_CONFIG;
  
  // CR316: Focus state for showing suggestions/recents
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // CR316: Get active preset ID for localStorage scoping
  const presetId = (context as any).presetId || null;
  
  // CR316: Recent searches hook
  const {
    recentSearches,
    addSearch,
    removeSearch,
    clearAll: clearRecentSearches,
  } = useRecentSearches({
    presetId,
    config: searchConfig.recentSearches,
    minChars: searchConfig.minChars,
  });
  
  // CR316: Search suggestions hook
  // Cast deals to expected type - the hook handles missing fields gracefully
  const suggestions = useSearchSuggestions({
    deals: (context.deals || []) as Array<{
      id?: string;
      canonical_deal_json?: Record<string, unknown>;
      merchant_name?: string;
      title?: string;
    }>,
    query: context.searchQuery,
    config: searchConfig.suggestions,
  });
  
  // Get enabled filters from layout configuration
  const enabledFilters: FilterControl[] = context.layout?.filters
    ?.filter((f) => f.enabled)
    ?.sort((a, b) => a.order - b.order) || [
      { id: "search", order: 0, enabled: true },
      { id: "category", order: 1, enabled: true },
      { id: "deal_type", order: 2, enabled: true },
      { id: "clear_all", order: 5, enabled: true },
    ];
  
  const handleClearAll = () => {
    context.onSearchChange("");
    context.onCategoryChange("All");
    context.onDealTypesChange("All");
  };
  
  // CR316: Handle search submission (add to recents)
  const handleSearchSubmit = useCallback((value: string) => {
    const trimmed = value.trim();
    if (trimmed.length >= searchConfig.minChars) {
      addSearch(trimmed);
    }
  }, [addSearch, searchConfig.minChars]);
  
  // CR316: Handle suggestion/recent selection
  const handleSearchSelect = useCallback((text: string) => {
    context.onSearchChange(text);
    addSearch(text);
    setIsFocused(false);
    inputRef.current?.blur();
  }, [context.onSearchChange, addSearch]);
  
  // CR316: Handle Enter key
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit(context.searchQuery);
      setIsFocused(false);
      inputRef.current?.blur();
    }
  }, [context.searchQuery, handleSearchSubmit]);

  return (
    <div className="py-3 px-4 border-b bg-muted/20 rounded-lg">
      <div className="flex flex-wrap gap-2 items-center">
        {enabledFilters.map((filter) => {
          if (filter.id === "search") {
            return (
              <div key={filter.id} className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                <Input
                  ref={inputRef}
                  placeholder={searchConfig.placeholderText || "Search deals..."}
                  className="pl-8 h-8 text-sm"
                  value={context.searchQuery}
                  onChange={(e) => context.onSearchChange(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => {
                    // Delay to allow click on dropdown
                    setTimeout(() => setIsFocused(false), 150);
                  }}
                  onKeyDown={handleKeyDown}
                />
                {/* CR316: Suggestions / Recent Searches Dropdown */}
                <SearchDropdownPanel
                  searchQuery={context.searchQuery}
                  isFocused={isFocused}
                  suggestionsConfig={searchConfig.suggestions}
                  recentSearchesConfig={searchConfig.recentSearches}
                  suggestions={suggestions}
                  recentSearches={recentSearches}
                  onSelect={handleSearchSelect}
                  onClearRecents={clearRecentSearches}
                  onRemoveRecent={removeSearch}
                />
              </div>
            );
          }
          
          if (filter.id === "category") {
            return (
              <Select
                key={filter.id}
                value={context.selectedCategory}
                onValueChange={context.onCategoryChange}
              >
                <SelectTrigger className="h-8 text-xs w-auto min-w-[100px]">
                  <SelectValue placeholder="category" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border z-50">
                  <SelectItem value="All">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category} value={cat.category}>
                      {cat.category} ({cat.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          
          if (filter.id === "deal_type") {
            return (
              <Select
                key={filter.id}
                value={context.selectedDealTypes === "All" ? "All" : context.selectedDealTypes.join(",")}
                onValueChange={(val) => {
                  if (val === "All") {
                    context.onDealTypesChange("All");
                  } else {
                    // For simplicity, single select from dropdown
                    context.onDealTypesChange([val as any]);
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs w-auto min-w-[100px]">
                  <SelectValue placeholder="deal type" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border z-50">
                  <SelectItem value="All">All Types</SelectItem>
                  <SelectItem value="BONUS">Bonus</SelectItem>
                  <SelectItem value="DISCOUNT">Discount</SelectItem>
                  <SelectItem value="POINTS">Points</SelectItem>
                  <SelectItem value="IN_KIND">In-Kind</SelectItem>
                </SelectContent>
              </Select>
            );
          }
          
          if (filter.id === "clear_all") {
            return (
              <Button 
                key={filter.id} 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            );
          }
          
          // Generic filter placeholder for promo_merchant, reward_brand, etc.
          return (
            <Select key={filter.id}>
              <SelectTrigger className="h-8 text-xs w-auto min-w-[100px]">
                <SelectValue placeholder={filter.id.replace(/_/g, " ")} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border z-50">
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          );
        })}
      </div>
    </div>
  );
}
